import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Site, SLD } from '@patsnap-utils/biz';
import type { AddFreeService } from '../add-free-service';
import { AddTrialApi } from '../../apis/add-free-api';
import { NeedAddTrialActionType } from '../../types';
import { initCookieService } from '@telecom/shared/utils/init-cookie.ts';

// https://confluence.zhihuiya.com/pages/viewpage.action?pageId=286641903
/** 赋权刷新次数计数的 sessionStorage 键 */
const MAX_TRIAL_STORAGE_KEY = 'EUREKA_IP_ADDTRIAL_COUNT';
/** 赋权刷新次数上限，超过则跳转 403 止血，防止无限刷新 */
const MAX_TRIAL_STORAGE_COUNT = 5;

@injectable()
export class AddTrialServiceImpl implements AddFreeService {
    private _enableAutoAddTrial = true;

    constructor(
        @inject(AddTrialApi)
        private _addTrialApi: AddTrialApi,
    ) {}

    async addFreePkg(grantedProductIds: string[]): Promise<NeedAddTrialActionType | string | undefined> {
        let actionType: NeedAddTrialActionType | string | undefined;
        try {
            if (grantedProductIds === undefined) {
                throw new Error('Granted_product_ids is undefined!');
            }

            // 已经在 account-status 落地页时不再触发准入检查，避免循环跳转
            if (window.location.pathname.includes('account-status')) {
                return;
            }

            const empowerType = this.getEmpowerType();
            const { action_type } = await this._addTrialApi.checkProductOnboarding(
                empowerType,
                initCookieService.getTrackingParams(),
            );
            actionType = action_type;
            this._dispatchOnboardingAction(actionType);
        } catch (error) {
            console.error('[AddTrial]', error);
        } finally {
            this._enableAutoAddTrial = false;
        }
        return actionType;
    }

    /**
     * 根据准入决策动作类型分发后续流程
     * @param actionType - 后端返回的准入决策动作类型
     */
    private _dispatchOnboardingAction(actionType: NeedAddTrialActionType | string): void {
        switch (actionType) {
        case NeedAddTrialActionType.DIRECT_LOGIN:
            // 已有权限，直接进入产品；赋权流程已收敛，清零刷新计数
            sessionStorage.removeItem(MAX_TRIAL_STORAGE_KEY);
            break;
        case NeedAddTrialActionType.GRANT_INDIVIDUAL_TENANT_PRIVILEGE:
            // 后端已在本次 check 请求中完成赋权，清缓存并刷新页面使权限生效
            this._reloadToApplyPrivilege();
            break;
        // CONTACT_ADMIN / SWITCH_* / CREATE_* 及未知类型：
        // 需要切换/创建个人租户或联系管理员，跳转 account-status 落地页引导
        default:
            this._redirectToAccountStatusPage();
            break;
        }
    }

    /**
     * 后端已赋权，清除本地缓存与 token 后刷新页面使新权限生效
     *
     * 使用 sessionStorage 计数防止异常情况下（如赋权未实际生效）无限刷新：
     * 刷新次数超过上限时跳转 403 止血，而非继续 reload。
     */
    private _reloadToApplyPrivilege(): void {
        const trialCounts = Number(sessionStorage.getItem(MAX_TRIAL_STORAGE_KEY) || 0);
        if (trialCounts >= MAX_TRIAL_STORAGE_COUNT) {
            sessionStorage.removeItem(MAX_TRIAL_STORAGE_KEY);
            window.location.href = new Site({ sld: SLD.EUREKA, pathname: '403/' }).href;
            throw new Error(`Trial counts is over ${MAX_TRIAL_STORAGE_COUNT}!`);
        }
        sessionStorage.setItem(MAX_TRIAL_STORAGE_KEY, String(trialCounts + 1));

        // 清除账号信息缓存
        localStorage.removeItem('patsnap_header_account_info');
        // 清除 token，强制重新获取带有新权限的 token
        localStorage.removeItem('patsnap-authorizations');
        window.location.reload();
    }

    /**
     * 跳转 account-status 落地页，并带上来源页地址用于操作完成后回跳
     */
    private _redirectToAccountStatusPage(): void {
        const { href } = new Site({ sld: SLD.EUREKA, pathname: 'account-status/' });
        window.location.href = `${href}?account_status_from=${encodeURIComponent(window.location.href)}`;
    }

    getEmpowerType(): string {
        // 复用 AIR 赋权链路（IpEmpowerService 已弃用），传 IP 会因赋错产品套餐导致死循环
        return 'AIR';
    }

    isAutoAddTrialEnabled(): boolean {
        return this._enableAutoAddTrial;
    }
}
